import { useEffect, useState, useMemo } from "react";
import { useQuery } from "react-query";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import { twentyFourHoursInMs } from "../../../../utils";
import { useLocation, useParams } from "react-router-dom";

// Fetch a list of layers to add to the map
const useLayers = () => {
  const [layers, setLayers] = useState([]);
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { datacategoryPath } = useParams();
  const { search } = useLocation();

  // Parse layer IDs from URL once, memoized for efficiency
  const layerIdsFromUrl = useMemo(() => {
    const searchParams = new URLSearchParams(search);
    const layersParam = searchParams.get("layers");
    return layersParam ? layersParam.split(",").map((id) => id.trim()) : [];
  }, [search]);

  const { data, isError, isLoading } = useQuery(
    ["Layers", isAuthenticated],
    async () => {
      let headers = {};
      if (isAuthenticated) {
        const token = await getAccessTokenSilently();
        headers = { Authorization: `Bearer ${token}` };
      }
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_ENDPOINT}/api/public-map/layers`,
          { headers }
        );
        return data;
      } catch (err) {
        console.error(err);
      }
    },
    {
      refetchOnWindowFocus: false,
      staleTime: twentyFourHoursInMs,
      cacheTime: twentyFourHoursInMs,
    }
  );

  // Update layers visibility based on data, datacategoryPath, and URL parameters
  useEffect(() => {
    if (data) {
      const updatedLayers = data.map((item) => {
        // Determine the group to check against based on the presence of datacategoryPath
        const checkGroup = datacategoryPath || "default";

        // Check if the layer should be visible based on:
        // 1. Its visibleOnLoadForGroups configuration
        // 2. Explicit inclusion in the URL layers parameter
        const fromGroup =
          item?.lreProperties?.visibleOnLoadForGroups?.includes(checkGroup);
        const fromUrl = layerIdsFromUrl.includes(item.id);
        const shouldBeVisible = fromGroup || fromUrl;

        if (fromUrl) {
          console.log(`Debug - Layer ${item.id} is visible from URL`);
        }

        if (shouldBeVisible) {
          return {
            ...item,
            layout: {
              ...item.layout,
              visibility: "visible",
            },
          };
        }
        return item;
      });
      setLayers(updatedLayers);
    }
  }, [data, datacategoryPath, layerIdsFromUrl]);

  return {
    isLoading,
    isError,
    layers,
    setLayers,
  };
};

export default useLayers;
